import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone, Clock, Shield, Recycle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, zones } from '../mock';
import * as LucideIcons from 'lucide-react';
import EstimationBanner from '../components/EstimationBanner';
import SEO from '../components/SEO';

const Home = () => {
  const whyChooseUs = [
    {
      icon: Clock,
      title: 'Intervention rapide',
      description: 'Devis gratuit et intervention sous 24-48h'
    },
    {
      icon: Recycle,
      title: 'Valorisation & recyclage',
      description: 'Tri sélectif et économie circulaire'
    },
    {
      icon: Shield,
      title: 'Équipe professionnelle',
      description: 'Assurée, équipée et formée'
    },
    {
      icon: CheckCircle2,
      title: 'Service complet',
      description: 'Tri, enlèvement, recyclage, nettoyage'
    }
  ];

  // Service images mapping with user-provided photos
  const serviceImages = {
    'maison': '/images/maison.jpg',
    'appartement': '/images/appartement.jpg',
    'cave-grenier': '/images/garage.jpg',
    'professionnel': '/images/professionnel.jpg',
    'succession': '/images/succession.jpg'
  };

  const serviceLinks = {
    'maison': '/services/maison',
    'appartement': '/services/appartement',
    'cave-grenier': '/services/cave-grenier',
    'professionnel': '/services/professionnel',
    'succession': '/services/succession'
  };

  const zoneLinks = {
    'annecy': '/zones/annecy',
    'mont-blanc': '/zones/mont-blanc',
    'aix-chambery': '/zones/aix-chambery',
    'genevois': '/zones/genevois',
    'pays-arve': '/zones/pays-arve',
    'pays-gex': '/zones/pays-gex'
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras et recyclage en Savoie, Haute-Savoie, Ain | DEBECO"
        description="Service professionnel de débarras, vide-maison et recyclage à Annecy, en Savoie (73), Haute-Savoie (74) et Ain (01). Devis gratuit."
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative text-white overflow-hidden min-h-[600px] flex items-center">
        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          poster="/images/annecy.jpg"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://customer-assets.emergentagent.com/job_clean-habitat/artifacts/90bw7dcz_Vide%CC%81o%20Lac%20Annecy.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/85 via-blue-900/75 to-transparent"></div>
        
        <div className="relative container mx-auto px-4 py-20 md:py-32 z-10">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Débarras de mobilier et vide-maison en Savoie et Haute-Savoie
            </h1>
            
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-emerald-300">Nos zones d'intervention :</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-lg">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Annecy et bassin Annécien</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Pays du Mont Blanc - Chablais - Aravis</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Aix-les-Bains - Chambéry - Maurienne - Tarentaise</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Genevois - Vallée de l'Arve - Pays de Gex</span>
                </li>
              </ul>
            </div>
            
            <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed">
              Notre entreprise est spécialisée dans le <strong>débarras mobilier</strong>, le <strong>vide-maison</strong> et l'<strong>enlèvement d'encombrants</strong>, pour les particuliers, professionnels, agences immobilières et notaires.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/devis">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl hover:shadow-2xl transition-all text-base px-6 py-6 w-full sm:w-auto">
                  Estimez le coût de votre débarras en 5 min
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0451401617">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 transition-all text-lg px-8 py-6 w-full sm:w-auto">
                  <Phone className="mr-2 w-5 h-5" />
                  04 51 40 16 17
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6 mt-10 pt-10 border-t border-blue-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-blue-100">Devis gratuit</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-blue-100">Recyclage responsable</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-blue-100">Intervention rapide</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Pourquoi nous choisir ?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Une expertise reconnue dans le débarras et le recyclage</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all group">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-blue-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Nos services de débarras</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Solutions complètes adaptées à tous vos besoins</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              const imageUrl = serviceImages[service.id] || service.image;
              const linkUrl = serviceLinks[service.id] || '/services';
              // Update shortDesc for professionnel
              const shortDesc = service.id === 'professionnel' ? 'Bureaux, commerces, locaux, archives' : service.shortDesc;
              return (
                <Card key={service.id} className="overflow-hidden hover:shadow-2xl transition-all group border-2 hover:border-emerald-500 flex flex-col">
                  <div className="h-48 bg-gradient-to-br from-blue-900 to-blue-800 relative overflow-hidden">
                    <img 
                      src={imageUrl} 
                      alt={service.title}
                      className="w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-110 transition-all duration-300"
                      loading="lazy"
                      width="400"
                      height="192"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      {IconComponent && <IconComponent className="w-16 h-16 text-white" />}
                    </div>
                  </div>
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <h3 className="font-bold text-xl text-blue-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4 flex-grow">{shortDesc}</p>
                    <Link to={linkUrl} className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-medium group/link mt-auto">
                      En savoir plus
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white">
                Voir tous les services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Nos zones d'intervention</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Nous intervenons rapidement en Savoie, Haute-Savoie et l'Ain</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {zones.map((zone) => {
              const linkUrl = zoneLinks[zone.id] || '/zones';
              return (
                <Card key={zone.id} className="border-2 hover:border-blue-900 hover:shadow-xl transition-all flex flex-col">
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <h3 className="font-bold text-xl text-blue-900 mb-3">{zone.title}</h3>
                    <p className="text-gray-600 mb-4 text-sm flex-grow">{zone.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {zone.cities.slice(0, 4).map((city, idx) => (
                        <span key={idx} className="text-xs bg-blue-100 text-blue-900 px-3 py-1 rounded-full">
                          {city}
                        </span>
                      ))}
                    </div>
                    <Link to={linkUrl} className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-medium group mt-auto">
                      Découvrir
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Testimonials - Google Reviews via Elfsight */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Ils nous font confiance</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">L'avis de nos clients</p>
          </div>
          
          {/* Elfsight Google Reviews Widget */}
          <div className="elfsight-app-fc3d11ac-abd8-4ad2-9693-4717133375e5" data-elfsight-app-lazy></div>
        </div>
      </section>

      {/* SEO Internal Links Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Services Links */}
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Nos prestations de débarras</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services/maison" className="text-gray-600 hover:text-emerald-600">Débarras de maison complète</Link></li>
                <li><Link to="/services/appartement" className="text-gray-600 hover:text-emerald-600">Vide-appartement professionnel</Link></li>
                <li><Link to="/services/cave-grenier" className="text-gray-600 hover:text-emerald-600">Débarras cave et grenier</Link></li>
                <li><Link to="/services/succession" className="text-gray-600 hover:text-emerald-600">Débarras succession et héritage</Link></li>
                <li><Link to="/services/professionnel" className="text-gray-600 hover:text-emerald-600">Débarras locaux professionnels</Link></li>
              </ul>
            </div>
            
            {/* Zones Links */}
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Zones d'intervention</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/zones/annecy" className="text-gray-600 hover:text-emerald-600">Débarras Annecy et agglomération</Link></li>
                <li><Link to="/zones/aix-chambery" className="text-gray-600 hover:text-emerald-600">Débarras Chambéry et Aix-les-Bains</Link></li>
                <li><Link to="/zones/genevois" className="text-gray-600 hover:text-emerald-600">Débarras Annemasse et Genevois</Link></li>
                <li><Link to="/zones/pays-gex" className="text-gray-600 hover:text-emerald-600">Débarras Pays de Gex</Link></li>
                <li><Link to="/zones/mont-blanc" className="text-gray-600 hover:text-emerald-600">Débarras Pays du Mont-Blanc</Link></li>
              </ul>
            </div>
            
            {/* Info Links */}
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Informations utiles</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/devis" className="text-gray-600 hover:text-emerald-600">Estimation gratuite en ligne</Link></li>
                <li><Link to="/faq" className="text-gray-600 hover:text-emerald-600">Questions fréquentes</Link></li>
                <li><Link to="/a-propos" className="text-gray-600 hover:text-emerald-600">À propos de DEBECO</Link></li>
                <li><Link to="/blog" className="text-gray-600 hover:text-emerald-600">Conseils et actualités</Link></li>
                <li><Link to="/contact" className="text-gray-600 hover:text-emerald-600">Nous contacter</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-emerald-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{backgroundImage: "url('/images/cta-bg.jpg')"}}></div>
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras rapide ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Obtenez votre devis gratuit en moins de 24h et bénéficiez d'une intervention rapide
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                Nous appeler
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
